import { Platform } from '@angular/cdk/platform';
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import {
    AfterViewInit, ChangeDetectionStrategy, Component, ContentChild, CUSTOM_ELEMENTS_SCHEMA, ElementRef, EventEmitter, forwardRef, HostListener, Inject, Input,
    OnDestroy, Output, TemplateRef,
    ViewChild
} from '@angular/core';
import { FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { PopoverOptions, slideVerticalTopAnimation } from '@inscomponents/core';
import { EventBus, EventBusBase } from '@skylab-angular-shared/core';
import { TranslatePipe } from '@skylab-angular-shared/pipes';
import { isObject } from '@skylab-shared/utils';
import { Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { LoaderComponent } from '../loader/loader.component';
import { LoaderDirective } from '../loader/loader.directive';
import { ValidateDirective } from '../validation/validate.directive';
import { QlValueAccessor } from '../value-accessors/ql-value-accessor';
import { TextValueAccessorDirective } from '../value-accessors/text-value-acessor.directive';
import { TextareaValueAccessorDirective } from '../value-accessors/textarea.value-accessor.directive';
import { PopoverController } from '../wc-controllers/popover.controller';
import { DynamicListDirective } from './dynamic-list.directive';
import { QuickListContentComponent, QuickListSelectEvent } from './quick-list-content.component';
import { defaultQLSettings, SearchDelay } from './quick-list.service';
import { IconComponent } from '../icon/icon.component';

@Component({
    selector: 'skl-quick-list-ins',
    templateUrl: './quick-list.component.html',
    styleUrls: ['./quick-list.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => QuickListInsComponent),
            multi: true,
        },
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ValidateDirective, NgTemplateOutlet, TextareaValueAccessorDirective, FormsModule, TextValueAccessorDirective, LoaderComponent, LoaderDirective, AsyncPipe, TranslatePipe, IconComponent]
})
export class QuickListInsComponent<T, Item = any> extends QlValueAccessor implements AfterViewInit, OnDestroy {
    @ContentChild(DynamicListDirective) public dynamicList!: DynamicListDirective<T>;

    @ViewChild('listRef', { static: false }) public set listRef(value: ElementRef<HTMLInsListElement>) {
        if (value) {
            const listElement = value.nativeElement;
            listElement.setBottomScrollOffset(QuickListInsComponent.ItemOptionHeight);
            this.dynamicList.items$.pipe(
                debounceTime(0),
                tap(items => {
                    this.currentListLength = items.length;
                    listElement.updateList();
                }),
                takeUntil(this.destroy$),
            ).subscribe();
        }
    }

    @ViewChild('listTemplate') public listTemplate!: TemplateRef<any>;

    /** @internal */
    @Input() public validationKey!: string | null | undefined | (undefined | null | string)[];

    /** @internal */
    @Input() public disabled!: boolean;

    /**
     * True, when QL can make item object from search input
     */
    @Input() public customItemFromInput = true;

     /**
      * Input placeholder text.
      */
     @Input() public placeholder = '';

    /**
     * Property of model assign to input text content.
     */
    @Input() public displayText = '';

    /**
     * True, when input component should be multiline.
     */
    @Input() public multiline = false;

    /**
     * True, when want to clean input after select.
     */
    @Input() public clearAfterSelect = false;

    /**
     * True, when want to show clear button
     */
    @Input() public clearButton = true;

    /**
     * True, when want to block input actions when value is selected.
     */
    @Input() public set locked(isLocked: boolean) {
        this.isLocked = isLocked;
    }

    /**
     * Emitted when input text content has changed.
     */
    @Output() public sklInput = new EventEmitter<string | undefined | null>();

    /**
     * Emitted when select.
     */
    @Output() public sklSelect = new EventEmitter<Item | undefined | null>();

    public isLoaderVisible$!: Observable<boolean>;
    public hasData$!: Observable<boolean>;
    public canLoad$!: Observable<boolean>;
    public textValue = '';
    public textChange$ = new Subject<string>();
    private inputClick$ = new Subject<Event>();
    private isOpen$ = new Subject<boolean>();
    private destroy$ = new Subject<void>();
    private isSearchActive = false;
    private isExpanded = false;
    private isLocked = false;
    private overlay?: HTMLInsPopoverElement;
    private currentListLength!: number;
    private currentTextValue!: string;
    private static ItemOptionHeight = 48;
    private static DebounceTime = 250;

    public constructor(
        @Inject(EventBus) protected override readonly eventBus: EventBusBase,
        public override readonly elm: ElementRef<HTMLElement>,
        private readonly platform: Platform,
        protected readonly popoverController: PopoverController,
    ) {
        super(elm, eventBus);
    }

    public async ngAfterViewInit(): Promise<void> {
        this.setupTextChangeSubscription();
        this.setupInputClickSubscription();
        this.setupObservables();
    }

    private setupTextChangeSubscription(): void {
        this.textChange$.pipe(
            distinctUntilChanged(),
            debounceTime(SearchDelay),
            switchMap(
                async text => text ? await this.search(text) : await this.dynamicList.loadFirstPage()
            ),
            tap(() => {
                if (this.currentTextValue) this.open();
            }),
            takeUntil(this.destroy$),
        ).subscribe();
    }

    private setupInputClickSubscription(): void {
        this.inputClick$.pipe(
            debounceTime(QuickListInsComponent.DebounceTime),
            switchMap(async () => await this.dynamicList.loadFirstPage()),
            tap(() => {
                this.open();
            }),
            takeUntil(this.destroy$),
        ).subscribe();
    }

    private setupObservables(): void {
        this.isLoaderVisible$ = this.dynamicList.isLoading$;
        this.hasData$ = this.dynamicList.items$.pipe(
            map(items => items.length > 0)
        );
        this.canLoad$ = this.dynamicList.itemsPerPage$.pipe(
            map(itemsCount => itemsCount >= defaultQLSettings.paginationSettings.pageSize)
        );
    }

    public ngOnDestroy(): void {
        this.overlay?.dismiss();
        this.destroy$.next();
    }

    public override registerOnChange(fn: (value: unknown) => void): void {
        super.registerOnChange(value => {
            fn(value);
        });
    }

    public override registerOnTouched(fn: () => void): void {
        super.registerOnTouched(() => {
            fn();
        });
    }

    public textChange(event: KeyboardEvent): void {
        if (event.key === 'Enter') {
            this.handleEnter(event);
            return;
        };

        if (event.key === 'Tab') return;

        const inputValue = (event.target as HTMLInputElement).value;
        this.textChange$.next(inputValue);
    }

    public async handleClick(event: Event): Promise<void> {
        const isStaticText = this.isInsInputType(event.target as HTMLInputElement);
        if (this.readonly || this.disabled || this.inputRef.nativeElement.value || (isStaticText && this.isMobile)) return;
        this.inputClick$.next(event);
    }

    private isInsInputType(element: HTMLElement): boolean {
        return element.nodeName === 'INS-INPUT' || element.nodeName === 'INS-TEXTAREA';
    }

    public override set value(value: Item | string) {
        if (value !== undefined && this.value !== value) {
            if (typeof value === 'string') {
                this.textValue = value;
            }
            if (isObject(value)) {
                this.textValue = this.textFromObjectValue(value);
            }
            if (!value) {
                this.textValue = '';
            }
        }
    }

    public footerClicked(): void {
        if (this.clearAfterSelect) {
            this.value = '';
            this.currentTextValue = '';
            this.inputRef.nativeElement.value = this.textValue;
        }
        this.close();
    }

    public async handleLoad(_: IntersectionObserverEntry): Promise<void> {
        await this.dynamicList.loadPage();
    }

    public handleInput($event: Event): void {
        const value = ($event.target as HTMLInputElement).value;
        this.currentTextValue = value;
        this.isSearchActive = true;
        this.sklInput.emit(value);
    }

    public handleSelection(event: Event): void {
        const elm = event.target as HTMLOptionElement;
        this.selectItem({ elm, value: elm.value });
    }

    public get isMobile(): boolean {
        return this.platform.ANDROID || this.platform.IOS;
    }

    public close(): Promise<boolean> {
        return !this.overlay ? Promise.resolve(false) : this.overlay.dismiss();
    }

    public handleEnter(event: Event): void {
        const { shiftKey, target } = event as KeyboardEvent;
        if (this.multiline && shiftKey || !this.currentTextValue) return;
        if (this.currentListLength) return;

        if (this.customItemFromInput && (this.isSearchActive || (!this.isSearchActive && !this.currentListLength))) {
            this.selectItem({
                elm: target as HTMLOptionElement,
                value: { [this.displayText]: this.currentTextValue }
            });
        }
    }

    private async open(): Promise<HTMLInsPopoverElement | undefined> {
        if (this.isExpanded || this.overlay) {
            return;
        }
        this.isOpen$.next(true);
        const overlay = this.overlay = await this.createList();
        overlay.onDidDismiss().then(() => {
            this.overlay = undefined;
            this.isExpanded = false;
            this.isOpen$.next(false);
        });
        await overlay.present();
        await overlay.updatePosition();
        this.isExpanded = true;
        this.isSearchActive = false;

        return overlay;
    }

    private get readonly(): boolean {
        return !!this.inputRef.nativeElement.shadowRoot?.querySelector('[readonly]');
    }

    private async search(value: string): Promise<string> {
        await this.dynamicList.search(value);
        this.isSearchActive = false;

        return value;
    }

    private createList(): Promise<HTMLInsPopoverElement> {
        return this.popoverController.create(this.popoverConfig(this.isMobile));
    }

    private popoverConfig(isMobile: boolean): PopoverOptions {
        const anchor = this.elm.nativeElement as HTMLElement;
        const desktopCssStyle = {
            '--ins-width': 'fit-content',
            '--ins-max-width': '100%',
            '--ins-min-width': `${anchor.offsetWidth}px`,
        };
        const desktopConfig = {
            anchor,
            cssStyle: desktopCssStyle,
        } as const;
        const mobileCssStyle = {
            '--ins-width': '100%',
            '--ins-height': '100%',
            '--ins-list-max-height': `${window.innerHeight - QuickListInsComponent.ItemOptionHeight}px`,
            '--ins-overflow-x': 'hidden',
            '--ins-overflow-y': 'hidden',
        };
        const mobileConfig = {
            cssClass: 'popover-full-width',
            cssStyle: mobileCssStyle,
            enterAnimation: slideVerticalTopAnimation.enterAnimation,
            leaveAnimation: slideVerticalTopAnimation.leaveAnimation,
        } as const;
        const commonConfig = {
            component: QuickListContentComponent,
            componentProps: {
                dynamicList: this.dynamicList,
                listTemplate: this.listTemplate,
                context: { $implicit: this },
            },
        } as const;

        return isMobile ? { ...commonConfig, ...mobileConfig } : { ...commonConfig, ...desktopConfig };
    }

    private textFromObjectValue(value: Item | string, key = this.displayText): string {
        const parsedValue = isObject(value) ? (value as any)[key]: value;

        return parsedValue === Object(parsedValue) ? JSON.stringify(parsedValue) : parsedValue ?? '';
    }

    private selectItem(event: QuickListSelectEvent): void {
        const { elm, value } = event;
        this.handleChangeEvent(elm, value);
        if (this.clearAfterSelect) {
            this.textChange$.next('');
            this.currentTextValue = '';
        }
        this.value = this.clearAfterSelect ? '' : value as Item;
        this.inputRef.nativeElement.value = this.textValue;
        this.sklSelect.emit(value as Item);
        this.close();
    }

    @HostListener('insClear', ['$event.target'])
    protected handleClearEvent(elm: unknown): void {
        this.handleChangeEvent(elm as HTMLInputElement, null);
        this.textChange$.next('');
        this.sklInput.emit('');
    }

    @HostListener('insBlur', ['$event.target'])
    protected override handleBlurEvent(elm: unknown): void {
        super.handleBlurEvent(elm as HTMLInputElement);
    }

    @HostListener('keydown', ['$event'])
    protected handleKeyDown(event: KeyboardEvent): void {
        const { key, ctrlKey } = event;

        if (key === 'Escape' && this.isExpanded) {
            event.preventDefault();
            event.stopImmediatePropagation();
            this.close();
            return;
        }

        if (key === 'Tab') {
            this.handleTab();
            return;
        }

        if (key === 'Delete' && this.isLocked) {
            this.handleDelete(event);
            return;
        }

        if (this.isLocked && this.lastValue) {
            const allowedKeys = ['Left', 'Right'];
            const ctrlAllowed = ['A', 'a', 'C', 'c'];

            if (!(allowedKeys.includes(key) || (ctrlKey && ctrlAllowed.includes(key)))) {
                event.preventDefault();
            }
        }
    }

    protected handleTab(): void {
        this.close();
    }

    protected handleDelete(event: KeyboardEvent): void {
        this.handleChangeEvent(event.target as HTMLOptionElement, null);
        this.textChange$.next('');
        event.preventDefault();
    }
}
